import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";

type Props = {
  value: string;
  onChange: (e164: string) => void;
  label?: string;
  error?: string | null;
};

type Country = {
  iso: string;
  dialCode: string;
  flag: string;
  label: string;
  maxLocal: number;
  groups: number[];
};

const COUNTRIES: Country[] = [
  { iso: "fr", dialCode: "33", flag: "🇫🇷", label: "France", maxLocal: 9, groups: [1, 2, 2, 2, 2] },
  { iso: "be", dialCode: "32", flag: "🇧🇪", label: "Belgique", maxLocal: 9, groups: [3, 2, 2, 2] },
  { iso: "es", dialCode: "34", flag: "🇪🇸", label: "Espagne", maxLocal: 9, groups: [3, 3, 3] },
  { iso: "gb", dialCode: "44", flag: "🇬🇧", label: "Royaume-Uni", maxLocal: 10, groups: [4, 3, 3] },
  { iso: "ch", dialCode: "41", flag: "🇨🇭", label: "Suisse", maxLocal: 9, groups: [2, 3, 2, 2] },
  { iso: "de", dialCode: "49", flag: "🇩🇪", label: "Allemagne", maxLocal: 11, groups: [3, 4, 4] },
  { iso: "it", dialCode: "39", flag: "🇮🇹", label: "Italie", maxLocal: 10, groups: [3, 3, 4] },
];

const formatLocal = (country: Country, digits: string): string => {
  const trimmed = digits.replace(/\D/g, "").slice(0, country.maxLocal);
  const out: string[] = [];
  let i = 0;
  for (const size of country.groups) {
    if (i >= trimmed.length) break;
    out.push(trimmed.slice(i, i + size));
    i += size;
  }
  return out.join(" ");
};

const detectCountry = (e164: string): Country => {
  if (!e164) return COUNTRIES[0];
  const digits = e164.replace(/\D/g, "");
  // Match the longest dial code first (none overlap today, but future-proof)
  return (
    [...COUNTRIES]
      .sort((a, b) => b.dialCode.length - a.dialCode.length)
      .find((c) => digits.startsWith(c.dialCode)) ?? COUNTRIES[0]
  );
};

const PhoneField: React.FC<Props> = ({
  value,
  onChange,
  label = "Téléphone",
  error,
}) => {
  const country = React.useMemo(() => detectCountry(value), [value]);

  const localDigits = React.useMemo(() => {
    const digits = (value || "").replace(/\D/g, "");
    return digits.startsWith(country.dialCode)
      ? digits.slice(country.dialCode.length)
      : "";
  }, [value, country]);

  const display = formatLocal(country, localDigits);

  const emit = (next: Country, rawLocal: string) => {
    // Strip non-digits, leading zeros (French/Belgian habit), and clamp.
    let digits = rawLocal.replace(/\D/g, "");
    while (digits.startsWith("0")) digits = digits.slice(1);
    digits = digits.slice(0, next.maxLocal);
    // Backend regex (RegexValidation::REGEX_PHONE_E164) requires a single
    // space between the country code and the local number: `+33 612345678`.
    onChange(digits ? `+${next.dialCode} ${digits}` : "");
  };

  const handleCountry = (e: SelectChangeEvent<string>) => {
    const next =
      COUNTRIES.find((c) => c.iso === String(e.target.value)) ?? COUNTRIES[0];
    emit(next, localDigits);
  };

  const handleLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
    emit(country, e.target.value);
  };

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <InputLabel shrink htmlFor="sed-phone">
        {label}
      </InputLabel>
      <OutlinedInput
        id="sed-phone"
        notched
        fullWidth
        label={label}
        value={display}
        onChange={handleLocal}
        placeholder="6 12 34 56 78"
        sx={{
          "& input": { flex: 1, minWidth: 0 },
        }}
        inputProps={{ inputMode: "tel", autoComplete: "tel-national" }}
        startAdornment={
          <Select
            value={country.iso}
            onChange={handleCountry}
            variant="standard"
            disableUnderline
            MenuProps={{
              PaperProps: { sx: { maxHeight: 340 } },
            }}
            sx={{
              mr: 1,
              minWidth: 92,
              "& .MuiSelect-select": {
                py: 0,
                pl: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                fontSize: 14,
              },
            }}
            renderValue={(iso) => {
              const c =
                COUNTRIES.find((x) => x.iso === iso) ?? COUNTRIES[0];
              return (
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                    {c.flag}
                  </Box>
                  <Box component="span" sx={{ color: "#4a5568" }}>
                    +{c.dialCode}
                  </Box>
                </Box>
              );
            }}
          >
            {COUNTRIES.map((c) => (
              <MenuItem key={c.iso} value={c.iso}>
                <Box
                  component="span"
                  sx={{ fontSize: 18, mr: 1, lineHeight: 1 }}
                >
                  {c.flag}
                </Box>
                <Box component="span" sx={{ flex: 1 }}>
                  {c.label}
                </Box>
                <Box
                  component="span"
                  sx={{ ml: 2, color: "#a0aec0", fontSize: 13 }}
                >
                  +{c.dialCode}
                </Box>
              </MenuItem>
            ))}
          </Select>
        }
      />
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default PhoneField;
